import { useMemo } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/Atomic/Atoms/Icon'
import { Button } from '@/Atomic/Molecules/Button'
import { FILTERS_DROPDOWN_WIDTH } from '@/Components/EntitySearchHeader/constants'
import { FiltersDropdown } from '@/Components/EntitySearchHeader/FiltersDropdown'
import {
  FormContext,
  type FormContextType,
  type FormValues,
} from '@/Components/FormContext'
import { DataClassName } from '@/Data/const'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useSmartPosition } from '@/hooks/useSmartPosition'
import { hasActivityTypeOptions } from '@/utils/activityTypeFilter'

// Relative on purpose: the jest module mapper resolves `@/…` before its
// stylesheet stub, so an aliased SCSS import would reach the test runner.
import '../../../../../Components/EntitySearchHeader/EntitySearchHeader.scss'

const PANEL_ID = 'activity-tab-filters-panel'

interface ActivityTabFiltersProps {
  values: FormValues
  onChange: (values: FormValues) => void
}

/**
 * "Filters" dropdown of the activity tab — the same panel as on the activity
 * overview, but driven by component state instead of URL parameters: the data
 * scope of a detail page is the contact, project or document itself, so a
 * filter parameter would narrow that entity instead of its activities
 * (CRM-1272).
 *
 * @param values Currently applied activity filters, keyed like the filter
 *   parameters of the overview (for example `type`).
 * @param onChange Called with the complete next filter set whenever the user
 *   changes or clears a filter. There is no Apply step — as in the overview
 *   dropdown, every edit is applied immediately.
 */
export const ActivityTabFilters: React.FC<ActivityTabFiltersProps> = ({
  values,
  onChange,
}) => {
  const { t } = useTranslation()
  const { buttonRef, menuIsOpen, setMenuIsOpen, toggleMenu } = useIconMenu({
    group: 'activity-tab-filters',
  })
  const { panelRef, position, hasMeasured } = useSmartPosition({
    isOpen: menuIsOpen,
    triggerRef: buttonRef,
    preferHorizontal: 'left',
    preferVertical: 'bottom',
    listenToScroll: true,
  })

  // The filter components read and write through a FormContext. Committing
  // right away — instead of staging like the mobile filter sheet — keeps the
  // behaviour identical to the overview dropdown, which has no Apply button.
  const formContext = useMemo<FormContextType>(
    () => ({
      values,
      setValues: (next) =>
        onChange(typeof next === 'function' ? next(values) : next),
      apply: () => onChange(values),
      cancel: () => onChange(values),
    }),
    [values, onChange],
  )

  const activeFilterCount = Object.keys(values).length
  const label =
    activeFilterCount > 0
      ? `${t('common.filters')} (${activeFilterCount})`
      : t('common.filters')
  const ariaLabel =
    activeFilterCount > 0
      ? `${t('common.filter.title')} - ${activeFilterCount} ${t('common.filter.active')}`
      : t('common.filter.title')

  // The type filter is the only field of this panel. Without types from the
  // schema it renders nothing, so the trigger stays hidden rather than opening an
  // empty panel.
  if (!hasActivityTypeOptions()) return null

  return (
    <div className="position-relative d-inline-block" ref={buttonRef}>
      <Button
        variant="outlined"
        size="sm"
        onClick={toggleMenu}
        buttonClassName="filter-button"
        aria-expanded={menuIsOpen}
        aria-haspopup="true"
        aria-controls={PANEL_ID}
        aria-label={ariaLabel}
      >
        <span>{label}</span>
        <Icon icon="chevron-down" size="md" />
      </Button>

      {menuIsOpen && (
        <div
          id={PANEL_ID}
          ref={panelRef}
          className={classNames(
            'o-dropdown-panel',
            'filter-list-panel',
            `pos-${position}`,
          )}
          style={{
            visibility: hasMeasured ? 'visible' : 'hidden',
            width: `${FILTERS_DROPDOWN_WIDTH}px`,
            zIndex: 1000,
          }}
          role="region"
          aria-label={t('common.filters')}
        >
          <FormContext.Provider value={formContext}>
            <FiltersDropdown
              showTypeFilter
              typeFilterConfig={{ dataClassName: DataClassName.ACTIVITY }}
              onClearAll={() => onChange({})}
              onClose={() => setMenuIsOpen(false)}
            />
          </FormContext.Provider>
        </div>
      )}
    </div>
  )
}
